import { useEffect, useState } from 'react';
import { getCookie } from 'helpers/index';
import { legacyTimestamp } from '../generateReport';
import {
  fetchCollection,
  generateStateKey,
  hbIf,
  hbStr,
  readGeneratedReport,
  storeGeneratedReport,
  type CachedGeneratedReport,
} from '../reportTabData';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5d — the "3D Model" tab (tabs/ahu_model/view/ahu_model.js +
 * tmpl/ahu_model.tmpl + tmpl/report.ahu.tmpl). What the legacy tab REALLY
 * does:
 *
 *  - 'generate:report:ahu_model' (ahu_model.js:63-81): render() the static
 *    shell SYNCHRONOUSLY at dispatch (:65-67 — no POST; the kit rightly has
 *    no REPORT_DISPATCH entry), then fetch collection.ahu_model
 *    (`/api/v1.0/ahu/units/<ahu_id>/validation`, collection/all.js:8-16),
 *    stash `{model: collection.toJSON()}` + a timestamp under the state key
 *    (:70-79) and renderReport into `.content-item` (:42-61).
 *  - renderReport (:42-61): report.ahu.tmpl — when
 *    `ahu.validation_error_messages.allow_generate` a real <form> POSTing
 *    /model/generate/ with target="model_view" (submitting opens the model
 *    view in a new window/tab named model_view), otherwise a disabled
 *    generate link + the validation-errors message linking the AHU board.
 *    The `.integer` comma loop (:50-55) is a no-op — report.ahu.tmpl has no
 *    .integer elements. setTimeStamp (:56) fills the header span. The
 *    `#print`/`#export_csv` disabling (:57-58) targets buttons the header
 *    template no longer renders (tmpl/main_header.tmpl — only
 *    #generate_report exists) — no-op.
 *  - 'open:generated_report:ahu_model' (:83-92) is LIVE: re-opening the tab
 *    re-renders from the cache and flips generated=true (the page's reopen
 *    flow, REOPENABLE_TABS).
 *
 * Backbone Collection.fetch on the (object) validation response wraps it in
 * a single model, so `model[0]` is the response object.
 */

type AhuRow = Record<string, unknown>;

interface AhuModelResult {
  models: AhuRow[];
  timeStamp: string;
}

function toRows(payload: unknown): AhuRow[] {
  // Backbone Collection.set: an array stays an array; a bare object becomes
  // a single model.
  if (Array.isArray(payload)) return payload as AhuRow[];
  if (payload && typeof payload === 'object') return [payload as AhuRow];
  return [];
}

function fromCache(entry: CachedGeneratedReport | undefined): AhuModelResult | null {
  if (!entry || !entry.models) return null;
  return { models: entry.models, timeStamp: entry.timeStamp };
}

/** report.ahu.tmpl:1-14 — the rendered report content. */
const AhuModelReport = ({ ahu }: { ahu: AhuRow }) => {
  const messages = (ahu?.validation_error_messages ?? {}) as Record<string, unknown>;
  const ahuId = hbStr(ahu?.id);
  return (
    <div className="ahu-model-content-wrapper">
      {hbIf(messages.allow_generate) ? (
        <form id="cad_generate" action="/model/generate/" method="post" target="model_view">
          <input type="hidden" name="id" value={ahuId} />
          <input type="hidden" name="csrfmiddlewaretoken" value={getCookie('csrftoken') ?? ''} />
          <button className="btn btn-generate" />
          <span>Generate 3D Model</span>
        </form>
      ) : (
        <>
          <a
            className="btn btn-generate disable-generate"
            href={`/ahu/detail/${ahuId}`}
            target="_blank"
            rel="noreferrer"
          />
          <span>
            The air handling unit has {hbStr(messages.errors_count)} validation errors. Go to the{' '}
            <a className="ahu-link" href={`/ahu/detail/${ahuId}`} target="_blank" rel="noreferrer">
              AHU Board
            </a>{' '}
            to correct your messages.
          </span>
        </>
      )}
    </div>
  );
};

const AhuModelTab = ({ report, mode, states, reopenKey }: GeneratedTabPaneProps) => {
  // Reopen mode renders straight from the cache (openGeneratedReport :83-92).
  const [result, setResult] = useState<AhuModelResult | null>(() =>
    mode === 'reopen' && reopenKey ? fromCache(readGeneratedReport(reopenKey)) : null
  );

  // Reopen key changes while mounted (report re-opened for another state).
  useEffect(() => {
    if (mode === 'reopen' && reopenKey) setResult(fromCache(readGeneratedReport(reopenKey)));
  }, [mode, reopenKey]);

  // Generate mode: fetch per dispatch state (multi-AHU fans out into N
  // dispatches; each legacy dispatch refetches and re-renders — sequential
  // here, so the last state's render wins deterministically).
  useEffect(() => {
    if (mode === 'reopen') return undefined;
    let cancelled = false;
    (async () => {
      for (const state of states ?? []) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const payload = await fetchCollection<unknown>(
            `/api/v1.0/ahu/units/${state.ahu_id}/validation`
          );
          const models = toRows(payload);
          const timeStamp = legacyTimestamp(); // generetionTimeStamp (:110-134)
          storeGeneratedReport(generateStateKey(state), { models, timeStamp });
          if (!cancelled) setResult({ models, timeStamp });
        } catch {
          // Legacy fetch failure: the .then chain never runs — the dotted
          // preloader simply stays (ahu_model.js:71).
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, states]);

  const ahu = result?.models[0];

  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {/* tmpl/ahu_model.tmpl:1-11 — tooltip has NO leading space; the
          time-stamp span starts empty and is filled by setTimeStamp (:136). */}
      <div className="container-header clearfix">
        <h3 className="left">
          <span className="group-name" tooltip={`: ${report.name}`}>
            Engineering
          </span>
          <span className="report-name">{report.name}</span>
          <span className="time-stamp">{result ? `Generated on: ${result.timeStamp}` : ''}</span>
        </h3>
      </div>
      <div className="content">
        <div className="content-item">
          {ahu ? <AhuModelReport ahu={ahu} /> : <div className="preloader dotted-preloader active" />}
        </div>
      </div>
    </div>
  );
};

export default AhuModelTab;
